import { CurrencyCode, currencyMap } from "@/lib/currency";

function isCompactEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem("number-formatting");
  return stored === "true";
}

function getCompactOptions(value: number, decimals?: number): Intl.NumberFormatOptions {
  if (isCompactEnabled()) {
    return {
      notation: "compact",
      ...getMinMaxDigitsForKRounding(value, decimals),
    };
  }
  return {
    notation: "standard",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };
}

export function getDigitsForKRounding(_value: number) {
  return 2;
}

export function getMinMaxDigitsForKRounding(value: number, decimals?: number) {
  const digits = decimals !== undefined ? decimals : value === 0 ? 0 : 2;

  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };
}

export function formatEuro(value = 0, options?: Intl.NumberFormatOptions, decimals?: number) {
  return formatNumber(value, {
    style: "currency",
    currency: "EUR",
    ...getCompactOptions(value, decimals),
    ...options,
  });
}

export function formatCurrency(
  value = 0,
  currencyCode?: CurrencyCode | string,
  options?: Intl.NumberFormatOptions,
  decimals?: number
) {
  const code = (currencyCode as CurrencyCode) ?? CurrencyCode.EUR;
  const currency = currencyMap[code] ?? currencyMap[CurrencyCode.EUR];
  const convertedValue = value * (currency.value ?? 1);

  return formatNumber(convertedValue, {
    style: "currency",
    currency: currency.code,
    ...getCompactOptions(convertedValue, decimals),
    ...options,
  });
}

export function formatNumber(value = 0, options?: Intl.NumberFormatOptions) {
  const isPercent = options?.style === "percent";
  const usingCompact = !isPercent && isCompactEnabled() && options?.notation !== "standard";
  const notationDefaults: Intl.NumberFormatOptions =
    options?.notation == null && !isPercent
      ? { notation: usingCompact ? "compact" : "standard" }
      : {};
  const compactFractionDefaults: Intl.NumberFormatOptions = usingCompact
    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
    : {};
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    ...notationDefaults,
    ...compactFractionDefaults,
    ...options,
  }).format(value);
}

export function formatPercentage(
  value = 0,
  options?: Intl.NumberFormatOptions,
  decimals: number = 2
) {
  return formatNumber(value, {
    style: "percent",
    ...options,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Normalize a "percent" value to the fraction expected by Intl percent formatting.
 *
 * Backend values are sometimes returned as ratios (e.g. -0.36 meaning -36%)
 * and sometimes as percentage points (e.g. -36 meaning -36%).
 *
 * This helper converts both into a fraction suitable for `style: "percent"`.
 */
export function toPercentFraction(value = 0) {
  const abs = Math.abs(value);
  // If it's already in percentage points, convert to ratio.
  return abs > 1 ? value / 100 : value;
}
